import React from 'react';
import {
  Checkbox,
  CommandButton, DefaultButton, mergeStyleSets, MessageBarType, PrimaryButton, Separator, Stack, Text,
  TextField,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import structuredClone from 'core-js/stable/structured-clone';
import { isMobile } from 'react-device-detect';
import Theme from '../../shared/Theme';
import MessageBar from '../../shared/MessageBar';
import FeatureUsage from './FeatureUsage';
import StackTokens from '../../shared/StackTokens';
import Store from '../../shared/state/Store';
import LinkButton from '../../shared/LinkButton';
import Routing from '../../shared/Routing';
import TextFieldBuffered from '../../shared/TextFieldBuffered';
import GraphUtils from '../../modeller/GraphUtils';
import AdminApi from '../../shared/util/AdminApi';
import AccountsState from './AccountsState';
import UiReducer from '../../shared/state/UiReducer';
import HelpButton from '../../shared/help/HelpButton';
import IconButton from '../../shared/IconButton';

const componentStyles = mergeStyleSets({
  refreshButton: {
    height: '22px',
    selectors: {
      i: {
        margin: 0,
        padding: 0,
      },
      span: {
        display: 'inline-block',
        width: 'min-content',
        paddingTop: '2px',
      },
    },
  },
  smallTextInput: {
    width: '50px',
  },
  addDomainButtonContainer: {
    verticalAlign: 'top',
    paddingTop: '7px',
  },
  addDomainButton: {
    width: '34px',
    selectors: {
      i: {
        fontSize: '24px',
        margin: 0,
      },
    },
  },
  ruleCheckbox: {
    width: 'fit-content',
  },
  ruleLabel: {
    fontSize: 'small',
  },
});

const controllableFeatures = [
  {
    name: 'subscriptionAdministration',
    label: 'Subscription administration (Admin Dashboard)',
    description: ['When enabled, allows the subscription owner to manage their subscription using the Admin Dashboard.'],
  },
  {
    name: 'globalEndUsers',
    label: 'Global end-users',
    description: [
      'When enabled, allows assigning global end-users that have access to all apps on this subscription.',
      'NOTE: This feature requires the "Subscription administration (Admin Dashboard)" feature to be enabled.',
    ],
    requires: ['subscriptionAdministration'],
    groupUnder: 'subscriptionAdministration',
  },
  {
    name: 'activationControl',
    label: 'Activation control',
    description: [
      'When enabled, allows managing local activations for license keys on this subscription.',
      'NOTE: This feature requires the "Subscription administration (Admin Dashboard)" feature to be enabled.',
    ],
    requires: ['subscriptionAdministration'],
    groupUnder: 'subscriptionAdministration',
  },
  {
    name: 'collaboration',
    label: 'Collaboration',
    description: ['When enabled, allows app owner to assign contributors to their app, allowing multiple users to collaborate on an app.'],
  },
  {
    name: 'publicPublishing',
    label: 'Public app publishing',
    description: ['When enabled, allows publishing apps with public access (no end-user registration required).'],
  }];

export default ({ subscriptionAccount, relatedAccounts, includeUsage }) => {
  const navigate = useNavigate();
  const isPrimarySubscription = subscriptionAccount.email === relatedAccounts.primaryAccount.email;
  const [globalState, dispatch] = Store.useStore();
  const [editable, setEditable] = React.useState(isPrimarySubscription && subscriptionAccount.subscription.manual);
  const [subscriptionUpdate, setSubscriptionUpdate] = React.useState(structuredClone(subscriptionAccount.subscription));
  const [changesMade, setChangesMade] = React.useState(false);
  const [newDomainUncapped, setNewDomainUncapped] = React.useState('');
  const [newDomainUncappedError, setNewDomainUncappedError] = React.useState('');
  const [newDomainCapped, setNewDomainCapped] = React.useState('');
  const [newDomainCappedError, setNewDomainCappedError] = React.useState('');
  const [showApplyNotice, setShowApplyNotice] = React.useState(false);

  React.useEffect(() => {
    if (isPrimarySubscription) {
      setSubscriptionUpdate(structuredClone(subscriptionAccount.subscription));
      setTimeout(() => {
        setChangesMade(false);
      }, 50);
    }
  }, [subscriptionAccount.subscription]);

  React.useEffect(() => {
    setEditable(isPrimarySubscription && subscriptionUpdate.manual);
  }, [subscriptionUpdate.manual]);

  React.useEffect(() => {
    setChangesMade(true);
  }, [subscriptionUpdate]);

  React.useEffect(() => {
    setChangesMade(false);
  }, []);

  React.useEffect(() => {
    if (changesMade) {
      setShowApplyNotice(true);
    }
  }, [changesMade]);

  const rules = [
    {
      type: 'entity',
      target: 'self',
      enabled: false,
    },
    {
      type: 'entity',
      target: 'team',
      enabled: false,
    },
    {
      type: 'entity',
      target: 'anyone',
      enabled: false,
    },
  ];
  subscriptionUpdate.endUserAssignmentRules.forEach((rule) => {
    switch (rule.target) {
      case 'self': rules[0].enabled = true; break;
      case 'team': rules[1].enabled = true; break;
      case 'anyone': rules[2].enabled = true; break;
      default:
    }
    if (rule.type === 'domain_uncapped' || rule.type === 'domain_capped') {
      rules.push({
        ...rule,
        enabled: true,
      });
    }
  });
  const addDomainCappedButtonRef = React.useRef();
  const addDomainUncappedButtonRef = React.useRef();

  const renderRuleRowHint = (rule) => {
    if (rule.type !== 'entity') {
      return <></>;
    }
    return (
      <HelpButton styles={{ root: { marginLeft: '5px', verticalAlign: 'sub' } }}>
        <div style={isMobile ? {} : {
          maxWidth: '45vw',
        }}
        >
          {rule.target === 'self' && (
            <Text block>App owner can assign themselves as an end-user.</Text>
          )}
          {rule.target === 'team' && (
          <>
            <Text block>App owner can assign any member of the team as an end-user.</Text>
            <Text block>The team is defined as all accounts that are assigned a license key from the same subscription.</Text>
          </>
          )}
          {rule.target === 'anyone' && (
            <Text block>Any email at all can be assigned as an end-user.</Text>
          )}
        </div>
      </HelpButton>
    );
  };

  const renderRuleRow = (rule) => {
    let label = rule.target;
    if (rule.type === 'entity') {
      label = label[0].toUpperCase() + label.slice(1);
    }
    if (rule.type === 'domain_uncapped') {
      label = `<Everyone> @${label}`;
    }
    if (rule.type === 'domain_capped') {
      label = `<Anyone> @${label}`;
    }
    return (
      <tr key={rule.type + rule.target}>
        <td>
          <Checkbox
            disabled={!isPrimarySubscription}
            className={[Theme.styles.tableRowCheckbox, componentStyles.ruleCheckbox].join(' ')}
            checked={rule.enabled}
            onChange={async () => {
              // console.log('Rule toggle', rule);
              const update = { ...subscriptionUpdate };
              if (rule.enabled) {
                // Find and remove this rule
                const index = subscriptionUpdate.endUserAssignmentRules.findIndex((fRule) => fRule.target === rule.target);
                subscriptionUpdate.endUserAssignmentRules.splice(index, 1);
              } else if (rule.type === 'entity') {
                const ruleCopy = { ...rule };
                delete ruleCopy.enabled;
                subscriptionUpdate.endUserAssignmentRules.push(ruleCopy);
              }
              setSubscriptionUpdate(update);
            }}
          />
        </td>
        <td>
          <span className={componentStyles.ruleLabel}>
            {label}
            {' '}
            {renderRuleRowHint(rule)}
          </span>
        </td>
      </tr>
    );
  };

  const renderDomainAddRow = (ruleType, buttonRef, newValue, setNewValue, errorText, setErrorText) => {
    if (!isPrimarySubscription) {
      return <></>;
    }
    return (
      <tr>
        <td className={componentStyles.addDomainButtonContainer}>
          <IconButton
            iconName="AddTo"
            styles={{
              icon: {
                fontSize: '22px !important',
                height: '22px !important',
                lineHeight: '22px !important',
              },
            }}
            buttonRef={buttonRef}
            onClick={async () => {
              const update = { ...subscriptionUpdate };
              const index = subscriptionUpdate.endUserAssignmentRules.findIndex((fRule) => fRule.target === newValue);
              if (index >= 0) {
                setErrorText('Domain already added');
                return;
              }
              if (!newValue.match('^[a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:.[a-zA-Z]{2,})+$')) {
                setErrorText('Invalid domain');
                return;
              }
              update.endUserAssignmentRules.push({
                type: ruleType,
                target: newValue,
              });
              setSubscriptionUpdate(update);
              setNewValue('');
            }}
          />
        </td>
        <td>
          <TextField
            value={newValue}
            onKeyDown={(event) => {
              if (event.keyCode === 13 || event.key === 'Enter' || event.key === 'NumpadEnter') {
                if (buttonRef.current) {
                  // eslint-disable-next-line no-underscore-dangle
                  buttonRef.current._onClick();
                }
              }
            }}
            onChange={(event, value) => {
              let fValue = `${value}`.trim().toLowerCase();
              fValue = fValue.replaceAll(/^(@|\.)/gi, '');
              setErrorText('');
              setNewValue(fValue);
            }}
            placeholder="Add domain"
            errorMessage={errorText}
          />
        </td>
      </tr>
    );
  };

  return (
    <Stack vertical>
      {(subscriptionAccount.email !== relatedAccounts.primaryAccount.email) && (
      <MessageBar
        styles={{ container: { width: 'fit-content' } }}
        text={(
          <>
            <Text block>
              {'This account is part of the '}
              <LinkButton
                text={subscriptionAccount.email}
                onClick={async () => {
                  navigate(`${Routing.getTree(globalState).home.routes.administration.routes.accounts.path}/${subscriptionAccount.uuid}`);
                }}
              />
              {' team'}
            </Text>
            <Text block>Navigate to the main account to manage its subscription</Text>
          </>
        )}
      />
      )}
      {isPrimarySubscription && (
      <Stack horizontal>
        <Checkbox
          className={Theme.styles.tableRowCheckbox}
          checked={subscriptionUpdate.manual}
          onChange={async () => {
            setSubscriptionUpdate({
              ...subscriptionUpdate,
              manual: !subscriptionUpdate.manual,
            });
          }}
          label="Manually set allowances"
        />
      </Stack>
      )}

      <div style={{ margin: '10px 0 0 0' }}>
        <table className={[Theme.styles.table, Theme.styles.tableNoPadding, Theme.styles.tableText].join(' ')}>
          <tbody>
            <tr>
              <th>ID</th>
              <td>:</td>
              <td>{subscriptionAccount.uuid}</td>
            </tr>
            <tr>
              <th>Subscription owner</th>
              <td>:</td>
              <td>{subscriptionAccount.email}</td>
            </tr>
            <tr>
              <th>Refreshed</th>
              <td>:</td>
              <td>
                <Stack horizontal tokens={StackTokens.spacing}>
                  <span>
                    {new Date(subscriptionAccount.lastSynced).toLocaleString(navigator.languages[0])}
                  </span>
                  <CommandButton
                    text=""
                    iconProps={{
                      iconName: 'Refresh',
                    }}
                    className={[Theme.styles.outlineControlButton, Theme.styles.commandButton, componentStyles.refreshButton].join(' ')}
                    onClick={async () => {
                      // console.log('Force-refresh account', subscriptionAccount.uuid);
                      const updatedAccount = await AdminApi.getAccount(subscriptionAccount.uuid, { refresh: true });
                      if (updatedAccount.responseStatus !== 200) {
                        dispatch(UiReducer.uiSetErrors(updatedAccount.messages));
                        return;
                      }

                      const fRelatedAccounts = { ...relatedAccounts };
                      if (fRelatedAccounts.primaryAccount.uuid === updatedAccount.uuid) {
                        fRelatedAccounts.primaryAccount = updatedAccount;
                      } else {
                        const index = fRelatedAccounts.secondaryAccounts.findIndex((account) => account.uuid === updatedAccount.uuid);
                        if (index >= 0) {
                          fRelatedAccounts.secondaryAccounts[index] = updatedAccount;
                        }
                      }

                      AccountsState.setRelatedAccounts(dispatch, fRelatedAccounts);

                      if (isPrimarySubscription) {
                        setSubscriptionUpdate(structuredClone(subscriptionAccount.subscription));
                      }
                    }}
                  />
                </Stack>
              </td>
            </tr>
            <tr>
              <th>Last seen</th>
              <td>:</td>
              <td>
                {new Date(subscriptionAccount.lastSeen).toLocaleString(navigator.languages[0])}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <header className={Theme.styles.sectionHeader}>Subscription Allowance</header>
      <div>
        <table className={[Theme.styles.table, Theme.styles.tableNoPadding, Theme.styles.tableText].join(' ')}>
          <tbody>
            <tr>
              <th>Apps saved to Cloud</th>
              <td>:</td>
              {includeUsage && (
              <>
                <td>{subscriptionAccount.subscription.totalCloudApps}</td>
                <td>/</td>
              </>
              )}
              <td>
                {editable ? (
                  <TextFieldBuffered
                    validateValue={(value) => {
                      let validatedValue;
                      try {
                        validatedValue = GraphUtils.validateInputValue({
                          value,
                          validationRules: { type: 'int', allowEmpty: false, min: 1 },
                        });
                      } catch (error) {
                        throw new Error(error.message);
                      }
                      return validatedValue;
                    }}
                    applyValue={(value) => {
                      setSubscriptionUpdate({
                        ...subscriptionUpdate,
                        maxCloudAppsPerCreator: value,
                      });
                    }}
                    actualValue={subscriptionUpdate.maxCloudAppsPerCreator}
                    className={componentStyles.smallTextInput}
                  />
                ) : (
                  <span>{subscriptionAccount.subscription.maxCloudAppsPerCreator}</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Published apps</th>
              <td>:</td>
              {includeUsage && (
              <>
                <td>{subscriptionAccount.subscription.totalPublishedApps}</td>
                <td>/</td>
              </>
              )}
              <td>
                {editable ? (
                  <TextFieldBuffered
                    validateValue={(value) => {
                      let validatedValue;
                      try {
                        validatedValue = GraphUtils.validateInputValue({
                          value,
                          validationRules: { type: 'int', allowEmpty: false, min: 1 },
                        });
                      } catch (error) {
                        throw new Error(error.message);
                      }
                      return validatedValue;
                    }}
                    applyValue={(value) => {
                      setSubscriptionUpdate({
                        ...subscriptionUpdate,
                        maxPublishedCloudApps: value,
                      });
                    }}
                    actualValue={subscriptionUpdate.maxPublishedCloudApps}
                    className={componentStyles.smallTextInput}
                  />
                ) : (
                  <span>{subscriptionAccount.subscription.maxPublishedCloudApps}</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Total users across all apps</th>
              <td>:</td>
              {includeUsage && (
              <>
                <td>{(subscriptionAccount.subscription.totalEndUsers || 0) + (subscriptionAccount.subscription.globalEndUsers.length || 0)}</td>
                <td>/</td>
              </>
              )}
              <td>
                {editable ? (
                  <TextFieldBuffered
                    validateValue={(value) => {
                      let validatedValue;
                      try {
                        validatedValue = GraphUtils.validateInputValue({
                          value,
                          validationRules: { type: 'int', allowEmpty: false, min: 1 },
                        });
                      } catch (error) {
                        throw new Error(error.message);
                      }
                      return validatedValue;
                    }}
                    applyValue={(value) => {
                      setSubscriptionUpdate({
                        ...subscriptionUpdate,
                        maxUsersTotal: value,
                      });
                    }}
                    actualValue={subscriptionUpdate.maxUsersTotal}
                    className={componentStyles.smallTextInput}
                  />
                ) : (
                  <span>{subscriptionAccount.subscription.maxUsersTotal}</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Users per app</th>
              <td>:</td>
              {includeUsage && (
              <>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </>
              )}
              <td>
                {editable ? (
                  <TextFieldBuffered
                    validateValue={(value) => {
                      let validatedValue;
                      try {
                        validatedValue = GraphUtils.validateInputValue({
                          value,
                          validationRules: { type: 'int', allowEmpty: false, min: 1 },
                        });
                      } catch (error) {
                        throw new Error(error.message);
                      }
                      return validatedValue;
                    }}
                    applyValue={(value) => {
                      setSubscriptionUpdate({
                        ...subscriptionUpdate,
                        maxUsersPerApp: value,
                      });
                    }}
                    actualValue={subscriptionUpdate.maxUsersPerApp}
                    className={componentStyles.smallTextInput}
                  />
                ) : (
                  <span>{subscriptionAccount.subscription.maxUsersPerApp}</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <table style={{ width: 'fit-content' }}>
        <tbody>
          <tr>
            <td colSpan="2">
              <header className={Theme.styles.sectionHeader}>End-user Assignment Rules</header>
            </td>
          </tr>
          {
            rules.filter((rule) => rule.type === 'entity').map((rule) => renderRuleRow(rule))
          }
          <tr>
            <td colSpan="2">
              <header className={Theme.styles.sectionSubHeader}>
                Named users from a domain
                <HelpButton styles={{ root: { marginLeft: '5px', verticalAlign: 'sub' } }}>
                  <div style={isMobile ? {} : {
                    maxWidth: '45vw',
                  }}
                  >
                    <Text block>Once a domain is added here, app owners on this subscription can assign any specific user from this domain as end-user for their app.</Text>
                    <Text block>Common use-case: A limited number of end-users from a corporate domain allowed.</Text>
                    <Text block>All such end-users will count towards the total end-user limit on the subscription.</Text>
                  </div>
                </HelpButton>
              </header>
            </td>
          </tr>
          {
            rules.filter((rule) => rule.type === 'domain_capped').map((rule) => renderRuleRow(rule))
          }
          {
            renderDomainAddRow('domain_capped', addDomainCappedButtonRef, newDomainCapped, setNewDomainCapped, newDomainCappedError, setNewDomainCappedError)
          }

          <tr>
            <td colSpan="2">
              <header className={Theme.styles.sectionSubHeader}>
                All users from a domain
                <HelpButton styles={{ root: { marginLeft: '5px', verticalAlign: 'sub' } }}>
                  <div style={isMobile ? {} : {
                    maxWidth: '45vw',
                  }}
                  >
                    <Text block>Once a domain is added here, app owners on this subscription can enable domain-wide access to the app.</Text>
                    <Text block>Anyone from the domain would be able to visit the app as its end-user without being named explicitly as such.</Text>
                    <Text block>Use-case: An unlimited number of end-users from a corporate domain allowed.</Text>
                    <Text block>None of these end-users will count towards the total end-user limit on the subscription, so use with caution.</Text>
                    <Text block>Usage allowances will apply as normal.</Text>
                  </div>
                </HelpButton>
              </header>
            </td>
          </tr>
          {
            rules.filter((rule) => rule.type === 'domain_uncapped').map((rule) => renderRuleRow(rule))
          }
          {
            renderDomainAddRow('domain_uncapped', addDomainUncappedButtonRef, newDomainUncapped, setNewDomainUncapped, newDomainUncappedError, setNewDomainUncappedError)
          }

        </tbody>
      </table>

      {showApplyNotice && changesMade && (
        <MessageBar
          styles={{ container: { width: 'fit-content' } }}
          text='Save all changes at once by clicking "Apply" below'
          messageBarType={MessageBarType.warning}
        />
      )}

      <header className={Theme.styles.sectionHeader}>Features</header>
      <Stack vertical>
        {(() => {
          // Sort features so grouped items appear after their parent
          const sortedFeatures = [];
          const processed = new Set();

          // First, add all non-grouped features
          controllableFeatures.forEach((feature) => {
            if (!feature.groupUnder && !processed.has(feature.name)) {
              sortedFeatures.push(feature);
              processed.add(feature.name);

              // Then add all features grouped under this one
              controllableFeatures.forEach((groupedFeature) => {
                if (groupedFeature.groupUnder === feature.name && !processed.has(groupedFeature.name)) {
                  sortedFeatures.push(groupedFeature);
                  processed.add(groupedFeature.name);
                }
              });
            }
          });

          return sortedFeatures.map((feature) => {
            const isGrouped = !!feature.groupUnder;
            const requiredFeatureEnabled = feature.requires
              ? feature.requires.every((req) => subscriptionUpdate.featuresEnabled.includes(req))
              : true;
            const labelColor = !requiredFeatureEnabled ? Theme.Colors.DisabledText : undefined;

            return (
              <Checkbox
                styles={{ label: { marginLeft: isGrouped ? '20px' : '0' } }}
                disabled={!isPrimarySubscription}
                className={Theme.styles.tableRowCheckbox}
                checked={subscriptionUpdate.featuresEnabled.includes(feature.name)}
                onChange={async () => {
                  const update = { ...subscriptionUpdate };
                  const index = update.featuresEnabled.findIndex((el) => el === feature.name);
                  if (index >= 0) {
                    update.featuresEnabled.splice(index, 1);
                  } else {
                    update.featuresEnabled.push(feature.name);
                  }
                  setSubscriptionUpdate(update);
                }}
                label={(
                  <Stack horizontal tokens={StackTokens.spacing5}>
                    <Text style={{ color: labelColor }}>{feature.label}</Text>
                    <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Help">
                      <div style={isMobile ? {} : {
                        maxWidth: '45vw',
                      }}
                      >
                        {feature.description.map((description, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Text block key={index}>{description}</Text>
                        ))}
                      </div>
                    </HelpButton>
                  </Stack>
                )}
                key={feature.name}
              />
            );
          });
        })()}
      </Stack>

      <header className={Theme.styles.sectionHeader}>Metered Usage</header>

      <Stack styles={{ inner: { margin: '-10px 0 0 -10px' } }} horizontal wrap tokens={StackTokens.spacingWide}>
        {
        Object.values(subscriptionAccount.subscription.featuresUsage)
          .map((featureUsage) => (
            <FeatureUsage
              featureUsage={featureUsage}
              key={featureUsage.feature}
              setSubscriptionUpdate={setSubscriptionUpdate}
              editable={editable}
              subscriptionUpdate={subscriptionUpdate}
            />
          ))
      }
      </Stack>

      {isPrimarySubscription && (
      <Stack vertical tokens={StackTokens.spacing}>
        <Separator />
        <Text>Apply changes made above to this subscription:</Text>
        <Stack horizontal tokens={StackTokens.spacing}>
          <PrimaryButton
            disabled={!changesMade}
            onClick={async () => {
              const updatedAccount = await AdminApi.updateSubscription(subscriptionAccount.uuid, subscriptionUpdate);
              if (updatedAccount.responseStatus !== 200) {
                dispatch(UiReducer.uiSetErrors(updatedAccount.messages));
                return;
              }
              AccountsState.setRelatedAccounts(dispatch, { ...relatedAccounts, primaryAccount: updatedAccount });
              setTimeout(() => {
                setChangesMade(false);
              }, 50);
            }}
            text="Apply"
            className={Theme.styles.outlineControlButton}
          />
          <DefaultButton
            disabled={!changesMade}
            onClick={() => {
              setSubscriptionUpdate(structuredClone(subscriptionAccount.subscription));
              setTimeout(() => {
                setChangesMade(false);
              }, 50);
            }}
            text="Cancel"
            className={Theme.styles.outlineControlButton}
          />
        </Stack>
      </Stack>
      )}

    </Stack>
  );
};
